/**
 * HoursRemainingSection
 *
 * Summary of required vs completed vs remaining hours with progress visualization.
 * Time-based context (e.g. annual). Mantine: Progress, Card, Stack, Text.
 * Medplum: no direct component; Mantine fills the gap.
 */

import { Card, Group, Progress, Stack, Text, Title } from '@mantine/core';
import type { HoursSummary } from '../types';

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

interface HoursRemainingSectionProps {
  summary: HoursSummary;
  /** Option B (learning-dashboard) uses a more prominent card layout */
  variant?: 'compliance-tracker' | 'learning-dashboard';
}

export function HoursRemainingSection({
  summary,
  variant = 'compliance-tracker',
}: HoursRemainingSectionProps): JSX.Element {
  const pct = summary.requiredHours > 0
    ? Math.min(100, Math.round((summary.completedHours / summary.requiredHours) * 100))
    : 0;
  const isComplete = summary.remainingHours <= 0;

  const content = (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm" fw={500} c="dimmed">
          {summary.periodLabel}
        </Text>
        <Text size="xs" c="dimmed">
          {formatDate(summary.periodStart)} – {formatDate(summary.periodEnd)}
        </Text>
      </Group>
      <Progress
        value={pct}
        color={isComplete ? 'green' : 'unifyGold'}
        size={variant === 'learning-dashboard' ? 'lg' : 'md'}
        radius="md"
        aria-label={`${summary.category} hours progress: ${pct}%`}
      />
      <Group justify="space-between">
        <Text size="sm">
          <Text span fw={600}>
            {summary.completedHours}
          </Text>
          {' / '}
          {summary.requiredHours} {summary.category} hours
        </Text>
        <Text size="sm" fw={500} c={isComplete ? 'green' : 'dimmed'}>
          {summary.remainingHours} remaining
        </Text>
      </Group>
    </Stack>
  );

  if (variant === 'learning-dashboard') {
    return (
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>Required hours remaining</Title>
          {content}
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder>
      <Text size="sm" fw={600} mb="xs">
        Required hours remaining
      </Text>
      {content}
    </Card>
  );
}
